/**
 * Helpers for turning Cognito errors into user-facing messages.
 *
 * Errors surfaced by utils/cognito-auth.ts carry a `.code` (e.g.
 * "CodeMismatchException") copied from Cognito's `__type`. This module
 * centralises the mapping from those codes to friendly messages so the auth
 * flows stay consistent.
 *
 * Two principles guide the messages:
 *  - On flows where the user has already proven control of the account (e.g.
 *    they hold a reset code), we can be specific about what went wrong.
 *  - On unauthenticated flows that take an email (request reset code, resend
 *    verification), we must NOT reveal whether an account exists or its state,
 *    to avoid user-enumeration. Those flows use a single neutral message for
 *    both success and existence/state errors — see AUTH_MESSAGES below.
 */

/** Read the Cognito error code regardless of how the error was surfaced. */
export const getCognitoErrorCode = (error: unknown): string | undefined => {
  if (error && typeof error === "object") {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string") return code;
    const name = (error as { name?: unknown }).name;
    if (typeof name === "string") return name;
  }
  return undefined;
};

export const AUTH_MESSAGES = {
  // Throttling — safe to surface, reveals nothing about the account.
  tooManyAttempts:
    "Too many attempts. Please wait a few minutes and try again.",

  // Reset-password step two. The user holds a valid code here, so these are
  // safe to be specific about.
  codeMismatch: "The verification code is incorrect. Please check and try again.",
  codeExpired: "This code has expired. Please request a new one.",
  invalidPassword:
    "Your new password doesn't meet the requirements. Please choose a stronger password.",
  passwordResetSuccess: "Your password has been successfully reset.",

  // Last-resort fallbacks.
  resetSubmitFailed:
    "We couldn't reset your password. Please try again or contact support.",
  genericError:
    "We encountered an unexpected error. Please try again in a few minutes or contact support.",
} as const;

/**
 * Message for the reset-password *submit* step (code + new password). The user
 * has proven control of the account via the code, so specific messages are
 * appropriate here.
 */
export const getResetPasswordSubmitMessage = (error: unknown): string => {
  switch (getCognitoErrorCode(error)) {
    case "CodeMismatchException":
      return AUTH_MESSAGES.codeMismatch;
    case "ExpiredCodeException":
      return AUTH_MESSAGES.codeExpired;
    case "InvalidPasswordException":
      return AUTH_MESSAGES.invalidPassword;
    case "LimitExceededException":
    case "TooManyRequestsException":
      return AUTH_MESSAGES.tooManyAttempts;
    default:
      return AUTH_MESSAGES.resetSubmitFailed;
  }
};

/**
 * Codes on an unauthenticated flow that reveal whether an account exists or its
 * state. These MUST be treated the same as success (neutral message) so an
 * attacker can't enumerate accounts.
 */
const ENUMERATION_CODES = new Set([
  "UserNotFoundException",
  "InvalidParameterException", // e.g. "User is already confirmed"
  "NotAuthorizedException",
]);

/**
 * Message to show when an unauthenticated flow (request reset code / resend
 * verification) errors.
 *
 * Returns `null` when the error reveals whether an account exists or its state
 * — the caller should then show its neutral success message so success and
 * these errors are indistinguishable. Throttling and genuine infrastructure
 * errors return a message to display.
 */
export const getUnauthErrorMessage = (error: unknown): string | null => {
  const code = getCognitoErrorCode(error);
  if (code === "LimitExceededException" || code === "TooManyRequestsException") {
    return AUTH_MESSAGES.tooManyAttempts;
  }
  if (code && ENUMERATION_CODES.has(code)) {
    return null; // neutralise — caller shows its neutral success message
  }
  return AUTH_MESSAGES.genericError;
};
